import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, ArrowUp, ArrowDown, RefreshCw, ImageIcon, Award, Megaphone, Bike, ExternalLink } from "lucide-react";
import { api } from "../lib/api";

// Дефолтные изображения статик-сайта (используются, пока в конфиге пусто)
const DEFAULT_MENU = ["01.webp?v=2", "02.webp?v=2"];
const DEFAULT_CERTS = ["2.webp", "3.webp", "4.webp", "5.webp", "6.webp", "7.webp"];

function ImageManager({ title, hint, icon: Icon, list, defaults, onChange, testPrefix }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const effective = list.length > 0 ? list : defaults;
  const usingDefaults = list.length === 0;

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const added = [];
    for (const file of Array.from(files)) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(data.url); // относительный /api/files/... — работает и на статик-сайте
      } catch (e) {
        const d = e.response?.data?.detail;
        toast.error(typeof d === "string" ? d : `Ошибка загрузки ${file.name}`);
      }
    }
    if (added.length > 0) {
      // Если ещё использовались дефолтные — берём их за основу, чтобы ничего не пропало
      onChange([...(usingDefaults ? defaults : list), ...added]);
      toast.success(`Загружено: ${added.length}`);
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const move = (i, dir) => {
    const arr = [...effective];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };

  const remove = (i) => {
    const arr = [...effective];
    arr.splice(i, 1);
    onChange(arr);
  };

  return (
    <div className="admin-card p-5 space-y-4" data-testid={`${testPrefix}-section`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center"><Icon size={20} className="text-[#EFAF04]" /></div>
          <div>
            <h2 className="font-display font-black text-lg leading-tight">{title}</h2>
            <p className="text-xs text-gray-500">{hint}</p>
          </div>
        </div>
        <div>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          <button data-testid={`${testPrefix}-upload-btn`} type="button" className="admin-btn" disabled={uploading} onClick={() => fileRef.current?.click()}>
            <Upload size={15} /> {uploading ? "Загрузка..." : "Загрузить фото"}
          </button>
        </div>
      </div>
      {usingDefaults && (
        <p className="text-xs font-semibold text-gray-400 bg-gray-50 rounded-xl px-3 py-2">
          Сейчас показываются стандартные изображения сайта. Загрузите свои или измените порядок — конфигурация сохранится на сервере.
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {effective.map((src, i) => (
          <div key={`${src}-${i}`} className="rounded-2xl border border-gray-200 overflow-hidden bg-white" data-testid={`${testPrefix}-item-${i}`}>
            <div className="aspect-[4/3] bg-gray-50">
              <img src={src.startsWith("/api/") || src.startsWith("http") ? src : `/${src}`} alt="" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex items-center justify-between px-2 py-1.5">
              <span className="text-[11px] font-bold text-gray-400">#{i + 1}</span>
              <div className="flex items-center gap-1">
                <button type="button" className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`${testPrefix}-up-${i}`} aria-label="Вверх"><ArrowUp size={14} /></button>
                <button type="button" className="p-1.5 rounded-lg hover:bg-gray-100 disabled:opacity-30" disabled={i === effective.length - 1} onClick={() => move(i, 1)} data-testid={`${testPrefix}-down-${i}`} aria-label="Вниз"><ArrowDown size={14} /></button>
                <button type="button" className="p-1.5 rounded-lg hover:bg-red-50 text-red-500" onClick={() => remove(i)} data-testid={`${testPrefix}-delete-${i}`} aria-label="Удалить"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
        {effective.length === 0 && <p className="col-span-full text-sm text-gray-400 font-semibold text-center py-6">Нет изображений — загрузите фото</p>}
      </div>
    </div>
  );
}

function pickLang(obj) {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj.ru || obj.uz || obj.en || "";
}

function BannerSelector({ ads, value, onChange }) {
  // value: undefined/null -> "default", "" -> "none", else banner id
  const sel = value == null ? "default" : (value === "" ? "none" : value);
  const current = ads.find((a) => a.id === sel);
  const previewAd = sel === "none" ? null
    : (current || ads.find((a) => /доставк/i.test(a.name || "")) || ads[0] || null);
  return (
    <div className="admin-card p-5 space-y-4" data-testid="header-banner-section">
      <div className="flex items-center gap-2.5">
        <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center"><Megaphone size={20} className="text-[#EFAF04]" /></div>
        <div>
          <h2 className="font-display font-black text-lg leading-tight">Баннер на главной</h2>
          <p className="text-xs text-gray-500">Между логотипом и слоганом. Блок — по размеру фото баннера (без полей и градиента)</p>
        </div>
      </div>
      <select
        data-testid="header-banner-select"
        className="admin-input w-full"
        value={sel}
        onChange={(e) => {
          const v = e.target.value;
          onChange(v === "default" ? "default" : v === "none" ? "" : v);
        }}
      >
        <option value="default">По умолчанию (Бесплатная доставка до 3 км)</option>
        <option value="none">Не показывать баннер</option>
        <optgroup label="Баннеры">
          {ads.map((a) => (
            <option key={a.id} value={a.id}>{a.name || pickLang(a.title) || a.id}</option>
          ))}
        </optgroup>
      </select>
      {previewAd ? (
        <div>
          <div
            data-testid="header-banner-preview"
            className={`relative overflow-hidden rounded-2xl w-full ${previewAd.image_url ? "" : `h-32 ${previewAd.dark ? "bg-[#111111]" : "bg-[#FEBC10]"}`}`}
          >
            {previewAd.image_url && <img src={previewAd.image_url} alt="" className="block w-full h-auto max-h-44 object-cover" />}
            {!previewAd.photo_only && (
              <div className={`absolute inset-y-0 left-0 ${previewAd.image_url ? "w-[64%]" : "w-full"} p-4 flex flex-col justify-center gap-1`}>
                <div className={`font-display font-black uppercase leading-tight text-base ${previewAd.image_url ? "text-white [text-shadow:0_1px_6px_rgba(0,0,0,0.6)]" : previewAd.dark ? "text-white" : "text-black"}`}>{pickLang(previewAd.title)}</div>
                <div className={`text-xs font-semibold ${previewAd.image_url ? "text-white [text-shadow:0_1px_5px_rgba(0,0,0,0.6)]" : previewAd.dark ? "text-gray-300" : "text-black/70"}`}>{pickLang(previewAd.description)}</div>
                {previewAd.tag && <span className="self-start bg-[#FF0000] text-white font-display font-black px-2.5 py-0.5 rounded-lg text-xs mt-1">{previewAd.tag}</span>}
              </div>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs font-semibold text-gray-400 bg-gray-50 rounded-xl px-3 py-2">Баннер на главной скрыт.</p>
      )}
    </div>
  );
}

function DeliveryButtonToggle({ enabled, onChange }) {
  const on = enabled !== false;
  return (
    <div className="admin-card p-5 space-y-3" data-testid="delivery-button-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-yellow-50 flex items-center justify-center"><Bike size={20} className="text-[#EFAF04]" /></div>
          <div>
            <h2 className="font-display font-black text-lg leading-tight">Кнопка «Заказать доставку»</h2>
            <p className="text-xs text-gray-500">На главной под слоганом. Ведёт на craft-oila.uz/delivery — страницу заказов UzBiznes</p>
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          data-testid="delivery-button-toggle"
          onClick={() => onChange(!on)}
          className={`relative inline-flex h-8 w-14 shrink-0 items-center rounded-full transition-colors ${on ? "bg-[#FEBC10]" : "bg-gray-300"}`}
        >
          <span className={`inline-block h-6 w-6 rounded-full bg-white shadow transition-transform ${on ? "translate-x-7" : "translate-x-1"}`} />
        </button>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className={`text-xs font-semibold rounded-xl px-3 py-2 ${on ? "text-green-700 bg-green-50" : "text-gray-500 bg-gray-50"}`} data-testid="delivery-button-status">
          {on ? "Кнопка показывается на главной, страница /delivery открыта." : "Кнопка скрыта. Страница /delivery показывает заглушку «Доставка временно недоступна»."}
        </p>
        <a href="/delivery" target="_blank" rel="noopener noreferrer" className="admin-btn admin-btn-ghost text-xs" data-testid="delivery-open-link">
          <ExternalLink size={14} /> Открыть /delivery
        </a>
      </div>
    </div>
  );
}

export default function Site() {
  const [cfg, setCfg] = useState(null);
  const [ads, setAds] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/site-config").then((r) => setCfg(r.data)).catch(() => toast.error("Ошибка загрузки"));
    api.get("/admin/ads").then((r) => setAds((r.data || []).filter((a) => a.active !== false))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    const next = { ...cfg, ...patch };
    setCfg(next);
    setBusy(true);
    try {
      await api.put("/admin/site-config", next);
      toast.success("Сохранено — изменения уже на сайте");
    } catch (e) { toast.error("Ошибка сохранения"); }
    setBusy(false);
  };

  if (!cfg) return <div className="flex justify-center py-16"><div className="w-9 h-9 border-4 border-[#FEBC10] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display font-black text-2xl">Сайт</h1>
          <p className="text-sm text-gray-500">Кнопка доставки, баннер, фото меню и сертификаты на главной странице craft-oila.uz</p>
        </div>
        <button className="admin-btn admin-btn-ghost" onClick={load} disabled={busy} data-testid="site-refresh-btn"><RefreshCw size={15} /> Обновить</button>
      </div>
      <DeliveryButtonToggle enabled={cfg.delivery_button_enabled} onChange={(v) => save({ delivery_button_enabled: v })} />
      <BannerSelector ads={ads} value={cfg.header_banner_id} onChange={(v) => save({ header_banner_id: v })} />
      <ImageManager
        title="Фото меню (карусель)"
        hint="Изображения меню на главной странице — листаются автоматически"
        icon={ImageIcon}
        list={cfg.menu_images || []}
        defaults={DEFAULT_MENU}
        onChange={(arr) => save({ menu_images: arr })}
        testPrefix="menu"
      />
      <ImageManager
        title="Сертификаты"
        hint="Галерея сертификатов в разделе «Сертификаты» на сайте"
        icon={Award}
        list={cfg.certificates || []}
        defaults={DEFAULT_CERTS}
        onChange={(arr) => save({ certificates: arr })}
        testPrefix="certs"
      />
    </div>
  );
}
